import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Trophy, ChevronLeft, ChevronRight, Calendar, ClipboardList, ListChecks,
} from "lucide-react";

const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const snapToMonday = (iso) => {
  const d = iso ? new Date(`${iso}T00:00:00`) : new Date();
  const wd = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - wd);
  return toISO(d);
};
const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return toISO(d);
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const scoreColor = (pct) => {
  if (pct === 0) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  if (pct >= -20) return "bg-amber-100 text-amber-800 border-amber-300";
  if (pct >= -50) return "bg-orange-100 text-orange-800 border-orange-300";
  return "bg-red-100 text-red-800 border-red-300";
};

export default function MyPerformance() {
  const [weekStart, setWeekStart] = useState(snapToMonday(""));
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/performance/my-weekly?week_start=${weekStart}`)
      .then(({ data }) => setRow(data.row))
      .catch(() => setRow(null))
      .finally(() => setLoading(false));
  }, [weekStart]);

  return (
    <div className="space-y-5" data-testid="my-performance-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <Trophy className="h-3 w-3" /> Weekly Scorecard
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Chivo" }}>
          My Performance
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Your delegation + checklist tasks for the selected week.
          On-time tasks carry no penalty. Each delayed or pending task counts as one miss.
        </p>
      </div>

      {/* Week selector */}
      <Card>
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
            <Calendar className="h-3 w-3" /> Week
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => setWeekStart(addDays(weekStart, -7))}
                  data-testid="my-week-prev-btn">
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <Input
            type="date"
            value={weekStart}
            onChange={(e) => setWeekStart(snapToMonday(e.target.value))}
            className="h-8 w-40 text-xs"
            data-testid="my-week-input"
          />
          <div className="text-xs text-slate-600">
            <b className="text-slate-900">{fmtDate(weekStart)}</b>
            {" → "}
            <b className="text-slate-900">{fmtDate(weekEnd)}</b>
          </div>
          <Button size="sm" variant="outline"
                  onClick={() => setWeekStart(addDays(weekStart, 7))}
                  data-testid="my-week-next-btn">
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => setWeekStart(snapToMonday(""))}
                  data-testid="my-this-week-btn">
            This Week
          </Button>
        </CardContent>
      </Card>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && row && (
        <>
          {/* Headline score */}
          <Card className="border-slate-200">
            <CardContent className="p-5 flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Score</div>
                <div className="text-5xl font-bold mt-1" style={{ fontFamily: "Chivo" }}>
                  <span
                    className={`inline-block px-4 py-1 rounded-lg border-2 tabular-nums ${scoreColor(row.score_pct)}`}
                    data-testid="my-score-pct"
                  >
                    {row.score_pct}%
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {row.total === 0
                    ? "No tasks due this week."
                    : row.score_pct === 0
                      ? "Perfect week — all tasks completed on time!"
                      : `${row.delayed + row.pending} miss${(row.delayed + row.pending) === 1 ? "" : "es"} out of ${row.total} task${row.total === 1 ? "" : "s"}.`}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Total tasks</div>
                <div className="text-3xl font-semibold tabular-nums mt-1" style={{ fontFamily: "Chivo" }}>
                  {row.total}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detail table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 text-right">
                        <ClipboardList className="h-3 w-3 inline mr-1" /> Delegation
                      </th>
                      <th className="px-3 py-2 text-right">
                        <ListChecks className="h-3 w-3 inline mr-1" /> Checklist
                      </th>
                      <th className="px-3 py-2 text-right text-emerald-700">On Time</th>
                      <th className="px-3 py-2 text-right text-amber-700">Delayed</th>
                      <th className="px-3 py-2 text-right text-red-700">Pending</th>
                      <th className="px-3 py-2 text-right">Score %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr data-testid="my-perf-row">
                      <td className="px-3 py-3 text-right tabular-nums text-lg">{row.delegation_task}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-lg">{row.checklist_task}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-lg text-emerald-700 font-semibold">{row.on_time}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-lg text-amber-700 font-semibold">{row.delayed}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-lg text-red-700 font-semibold">{row.pending}</td>
                      <td className="px-3 py-3 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded border text-sm font-semibold tabular-nums ${scoreColor(row.score_pct)}`}>
                          {row.score_pct}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Help */}
          <Card className="border-blue-200 bg-blue-50/40">
            <CardContent className="p-3 text-xs text-slate-700 leading-relaxed">
              <b>How is this calculated?</b><br />
              Misses = Delayed + Pending. Score% = -(Misses ÷ Total) × 100.
              {" "}A perfect week = 0%. The more misses, the more negative the score.
              {" "}Only tasks with a due date inside this week count.
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
